import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FileText,
  Calendar,
  Clock,
  AlertCircle,
  CheckCircle,
  Download,
  BookOpen,
  Award
} from 'lucide-react';

const AdmissionTestCard = ({ testConfig, onRegister, isRegistered, registration }) => {
  const [showDetails, setShowDetails] = useState(false);

  if (!testConfig || !testConfig.enabled) {
    return null;
  }

  const isRegistrationClosed = !testConfig.registrationOpen;
  const hasAdmitCard = registration?.admitCardUrl;

  // Check if last date has passed
  const isLastDatePassed = testConfig.lastDateToRegister 
    ? new Date(testConfig.lastDateToRegister) < new Date()
    : false;

  // Registration is closed if manually closed OR last date has passed
  const cannotRegister = isRegistrationClosed || isLastDatePassed;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`admission-test-card bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-xl shadow-lg p-6 border-2 transition-all duration-300 ${
        testConfig.mandatory 
          ? 'border-red-400 dark:border-red-600' 
          : 'border-orange-200 dark:border-orange-800'
      }`}
    >
      {/* Mandatory Test Notice */}
      {testConfig.mandatory && !isRegistered && (
        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 rounded-lg border-2 border-red-300 dark:border-red-700">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-800 dark:text-red-200">
                ⚠️ Mandatory Requirement
              </p>
              <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                You must register for this admission test before applying for admission to this institution.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
          <FileText className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              {testConfig.testName || 'Admission Test'}
            </h3>
            {testConfig.mandatory && (
              <span className="px-2 py-1 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 text-xs font-semibold rounded-full">
                Mandatory
              </span>
            )}
          </div>

          <div className="space-y-2 mb-4">
            {testConfig.testDate && (
              <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <Calendar className="w-4 h-4 text-orange-600" />
                <span>
                  <strong>Date:</strong> {new Date(testConfig.testDate).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
              </div>
            )}
            {testConfig.testTime && (
              <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <Clock className="w-4 h-4 text-orange-600" />
                <span><strong>Time:</strong> {testConfig.testTime}</span>
              </div>
            )}
            {testConfig.duration && (
              <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <Clock className="w-4 h-4 text-orange-600" />
                <span><strong>Duration:</strong> {testConfig.duration}</span>
              </div>
            )}
            {testConfig.totalMarks && (
              <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <Award className="w-4 h-4 text-orange-600" />
                <span><strong>Total Marks:</strong> {testConfig.totalMarks}</span>
              </div>
            )}
            {testConfig.registrationFee > 0 && (
              <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <span className="text-orange-600 font-bold">₹</span>
                <span><strong>Registration Fee:</strong> ₹{testConfig.registrationFee}</span>
              </div>
            )}
            {testConfig.lastDateToRegister && (
              <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <AlertCircle className="w-4 h-4 text-orange-600" />
                <span>
                  <strong>Last Date:</strong> {new Date(testConfig.lastDateToRegister).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
              </div>
            )}
          </div>

          {/* Registration Status */}
          {isRegistered && (
            <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/30 rounded-lg border border-green-300 dark:border-green-700">
              <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
                <CheckCircle className="w-5 h-5" />
                <span className="font-semibold">You are registered for this test</span>
              </div>
              
              {/* Test Result */}
              {registration?.testResult && (
                <div className={`mt-2 flex items-center gap-2 font-semibold ${
                  registration.testResult === 'selected'
                    ? 'text-green-700 dark:text-green-300'
                    : 'text-red-700 dark:text-red-300'
                }`}>
                  {registration.testResult === 'selected' ? (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      <span>Test Result: Selected ✓</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-5 h-5" />
                      <span>Test Result: Not Selected</span>
                    </>
                  )}
                </div>
              )}
              
              {hasAdmitCard && (
                <a
                  href={registration.admitCardUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-2 text-sm text-green-700 dark:text-green-300 hover:text-green-900 dark:hover:text-green-100 font-medium"
                >
                  <Download className="w-4 h-4" />
                  Download Admit Card
                </a>
              )}
              {!hasAdmitCard && (
                <p className="mt-1 text-sm text-green-700 dark:text-green-300">
                  Admit card will be available soon
                </p>
              )}
            </div>
          )}

          {/* Registration Closed Notice */}
          {cannotRegister && !isRegistered && (
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 rounded-lg border border-red-300 dark:border-red-700">
              <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
                <AlertCircle className="w-5 h-5" />
                <span className="font-semibold">
                  {isLastDatePassed ? 'Registration Deadline Passed' : 'Registration Closed'}
                </span>
              </div>
              <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                {isLastDatePassed 
                  ? 'The last date to register for this test has passed. Please contact the institution for more information.'
                  : 'Test registrations are currently closed by the institution.'
                }
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            {!isRegistered && !cannotRegister && (
              <button
                onClick={onRegister}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-semibold flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                Register for Test
              </button>
            )}
            
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="px-4 py-2 bg-white dark:bg-gray-800 text-orange-600 dark:text-orange-400 border-2 border-orange-600 dark:border-orange-400 rounded-lg hover:bg-orange-50 dark:hover:bg-gray-700 font-semibold flex items-center gap-2"
            >
              <BookOpen className="w-4 h-4" />
              {showDetails ? 'Hide Details' : 'View Details'}
            </button>
          </div>

          {/* Detailed Information */}
          {showDetails && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 pt-4 border-t border-orange-200 dark:border-orange-800 space-y-3"
            >
              {testConfig.syllabus && (
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Syllabus:</h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">
                    {testConfig.syllabus}
                  </p>
                </div>
              )}
              {testConfig.examPattern && (
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Exam Pattern:</h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">
                    {testConfig.examPattern}
                  </p>
                </div>
              )}
              {testConfig.passingMarks && (
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Passing Marks:</h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    {testConfig.passingMarks}
                  </p>
                </div>
              )}
              {testConfig.instructions && (
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Instructions:</h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">
                    {testConfig.instructions}
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default AdmissionTestCard;
